import React from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import EditNoteRounded from '@mui/icons-material/EditNoteRounded';
import TrackChangesRounded from '@mui/icons-material/TrackChangesRounded';
import RateReviewRounded from '@mui/icons-material/RateReviewRounded';
import GavelRounded from '@mui/icons-material/GavelRounded';
import SegmentRounded from '@mui/icons-material/SegmentRounded';
import FactCheckRounded from '@mui/icons-material/FactCheckRounded';
import SwapHorizRounded from '@mui/icons-material/SwapHorizRounded';
import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded';
import {
  LandingContainer,
  Section,
  Container,
  AnimateOnScroll,
  CenteredHeader,
  SectionEyebrow,
  SectionTitle,
  SectionSubtitle,
  landingHeroStyles,
} from '../home/HomePage.styles';
import { SiteHeader } from '../../shared/components/SiteHeader';
import { Footer } from '../../shared/components/Footer';
import SEO from '../../shared/components/SEO';
import ConnectorGlobe from '../../shared/components/ConnectorGlobe';
import OfficeWindowAnimation from '../../shared/components/OfficeWindowAnimation';
import CapabilityShowcase from '../home/CapabilityShowcase';
import CoreCapabilities, { CtaRow, FaqList, SurfaceLink, WorkflowContrast } from './CoreCapabilities';
import { useScrollReveal } from '../../shared/components/useScrollReveal';
import { FAQ_REPOSITORY } from '../../shared/services/faqChatService';

// =============================================================================
// WORD PAGE (/word)
// -----------------------------------------------------------------------------
// The third product surface: Restack for Word (drafting + clean tracked-change
// redlines), and how it works across Excel and PowerPoint. Visual-first: the
// hero is the shared OfficeWindowAnimation (app="word", scenario="word-report"),
// a full Word window with the integrated Restack sidebar drafting and redlining
// a diligence memo. Page spine matches /product and /excel: hero (headline,
// subhead, ONE primary CTA, proof, hero visual) -> problem to solution ->
// cross-app -> benefits -> how it works -> capabilities -> connectors -> who it
// is for -> FAQ -> risk reversal + closing CTA. House style: no em dashes.
// =============================================================================

const ACCENT = '#B83B1D';
const INK = '#2E140A';
// Word's brand blue (#2B579A) reads cold against the warm ivory marketing
// surface, so the PAGE accent uses a deepened, slightly desaturated slate-blue
// that harmonizes with the cream background. The brighter Word blue is kept
// only for the in-document tracked-change insertion (INS_BLUE), which sits on
// white in the cross-app memo mock.
const WORD_BLUE = '#35548A';
const INS_BLUE = '#2B579A';
const DEL_RED = '#C0392B';
const EXCEL_GREEN = '#217346';

const wordCapabilities = [
  {
    icon: <EditNoteRounded />,
    title: 'Drafts and rewrites',
    desc: 'Write an IC memo section, tighten a board pre-read, or answer comments in a diligence memo. You decide what lands.',
  },
  {
    icon: <TrackChangesRounded />,
    title: 'Surgical, clean redlines',
    desc: 'A one-word change records as a one-word revision, not a deleted-and-reinserted paragraph.',
  },
  {
    icon: <RateReviewRounded />,
    title: 'Approves substance with you',
    desc: 'Typos and formatting go straight in. Meaning-changing edits surface for your approval first.',
  },
  {
    icon: <GavelRounded />,
    title: 'Builds the memo from your materials',
    desc: 'Point it at the model outputs, diligence notes, and call transcripts; it drafts the IC memo or board pre-read sections with claims cited.',
  },
  {
    icon: <SegmentRounded />,
    title: 'Cleans up structure',
    desc: 'Fixes list numbering, headings, and styles through Word\'s real engines, keeping the file well-formed.',
  },
  {
    icon: <FactCheckRounded />,
    title: 'Researches and cites',
    desc: 'Pulls material from authoritative sources, with citations.',
  },
];

const wordPrompts = [
  '"Draft the risks and mitigants section of the IC memo from my diligence notes."',
  '"Turn the findings in this document into a two-page executive summary for the board pre-read."',
  '"Redline the risks and mitigants section to reflect the latest diligence findings."',
  '"Summarize what changed across the last two turns of the LOI."',
  '"Find every use of \'Adjusted EBITDA\' and make the definition consistent."',
];

const crossPrompts = [
  'In Word: "Pull the latest figures from my Excel model into the financial summary."',
  'In PowerPoint: "Pull the executive summary from my open Word memo onto slide 2."',
];

// One offer, one action: the same label in the hero and in the closing band.
const PRIMARY_CTA_LABEL = 'See it on your own document';

// -----------------------------------------------------------------------------
// PROBLEM TO SOLUTION. The drafting and redline cycle as it actually runs, then
// the same cycle with Restack in the document. The contrast is in the steps, not
// in numbers: there are no metrics here on purpose.
// -----------------------------------------------------------------------------
const workflowContrast = [
  {
    step: 'Draft',
    before: 'The IC memo gets rebuilt section by section from the last one, while the diligence notes sit in another file.',
    after: 'Point Restack at the model outputs, the notes, and the transcripts, and it drafts the sections with claims cited.',
  },
  {
    step: 'Redline',
    before: 'A one-word change comes back as a deleted-and-reinserted paragraph, so the reviewer rereads the whole thing.',
    after: 'A one-word change records as a one-word revision, so the reviewer sees only what actually moved.',
  },
  {
    step: 'Review',
    before: 'Typo fixes and meaning-changing edits arrive in the same undifferentiated pile of tracked changes.',
    after: 'Typos and formatting go straight in. Edits that change meaning surface for your approval first.',
  },
  {
    step: 'Tidy',
    before: 'Defined terms drift between sections and the list numbering breaks, and nobody catches it until the partner read.',
    after: "Restack reads the document back, makes a definition consistent wherever it appears, and repairs numbering and styles through Word's own engines.",
  },
];

// -----------------------------------------------------------------------------
// FAQ. Pulled by id from FAQ_REPOSITORY so this page and the support widget
// serve the same answers. The `refund` entry carries the risk reversal.
// -----------------------------------------------------------------------------
const WORD_FAQ_IDS = [
  'what-is-restack',
  'how-works',
  'different-from-chatgpt',
  'research-quality',
  'powerpoint-versions',
  'security',
  'pricing',
  'collaboration',
  'contact',
  'refund',
];
const WORD_FAQ = WORD_FAQ_IDS
  .map((id) => FAQ_REPOSITORY.find((entry) => entry.id === id))
  .filter(Boolean);

const WORD_FAQ_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: WORD_FAQ.map(({ question, answer }) => ({
    '@type': 'Question',
    name: question,
    acceptedAnswer: { '@type': 'Answer', text: answer },
  })),
};

// The risk reversal, verbatim from the entry the FAQ also renders.
const REFUND_COPY = FAQ_REPOSITORY.find((entry) => entry.id === 'refund')?.answer || '';

export default function WordPage() {
  const theme = useTheme();
  const hair = alpha(INK, 0.09);

  // Capture-phase scroll reveal (these pages scroll inside LandingContainer).
  // Scroll reveal for the animated blocks on this page.
  const isVisible = useScrollReveal();

  const eyebrowSx = {
    fontFamily: theme.typography.fontFamily,
    fontSize: '0.68rem',
    fontWeight: 600,
    letterSpacing: '0.16em',
    textTransform: 'uppercase',
    color: ACCENT,
  };

  return (
    <LandingContainer data-theme="light">
      <SEO
        title="Restack for Word | The Drafting and Redline Engine for Private Equity"
        description="The drafting and redline engine for Private Equity. Restack drafts and redlines in your live Office files, with clean tracked changes you accept or reject and claims checked and cited, on your firm's template."
        canonical="/word"
        schema={WORD_FAQ_SCHEMA}
      />
      <style>{landingHeroStyles}</style>

      <SiteHeader offsetTop={0} />

      <Box component="main" id="main-content">
        {/* 1. HERO. Outcome headline and audience, the single primary CTA with the
            Microsoft Store credential beside it, then a full Word window with the
            Restack AI agent working inside. */}
        <Section id="word-hero" sx={{ pt: { xs: 11, md: 16 } }}>
          <Container>
            <AnimateOnScroll isVisible={isVisible('word-hero-header')} data-animate-id="word-hero-header">
              <CenteredHeader sx={{ mb: { xs: 3, md: 4 } }}>
                <SectionEyebrow>Restack for Word</SectionEyebrow>
                <SectionTitle component="h1">
                  The drafting and redline engine for Private Equity, in your <em>live document.</em>
                </SectionTitle>
                <SectionSubtitle>
                  The diligence memo and the redline: clean, surgical tracked changes you accept or reject, with
                  claims checked and cited instead of whole-paragraph replacements.
                </SectionSubtitle>
              </CenteredHeader>
              {/* The CTA sits outside CenteredHeader: that component forces
                  `margin: 0 auto` on every direct child, which would fight the
                  row's own vertical spacing. */}
              <CtaRow label={PRIMARY_CTA_LABEL} theme={theme} sx={{ mb: { xs: 6, md: 8 } }} />
            </AnimateOnScroll>

            {/* Hero visual: the shared Office window animation, a full Word window
                with the integrated Restack sidebar drafting and redlining a memo. */}
            <AnimateOnScroll isVisible={isVisible('word-hero-visual')} delay={0.12} data-animate-id="word-hero-visual">
              <OfficeWindowAnimation app="word" scenario="word-report" />
            </AnimateOnScroll>
          </Container>
        </Section>

        {/* 2. PROBLEM TO SOLUTION. Where the hours go in the memo cycle, and what
            changes with Restack in the document. */}
        <Section id="word-rebuild">
          <Container>
            <AnimateOnScroll isVisible={isVisible('rebuild-header')} data-animate-id="rebuild-header">
              <CenteredHeader>
                <SectionEyebrow>The rebuild</SectionEyebrow>
                <SectionTitle component="h2">The same memo, <em>redrafted every deal</em>.</SectionTitle>
                <SectionSubtitle>
                  The structure barely changes between deals, and the redline round trips regardless.
                  Here is where the hours go, and what changes with Restack in the document.
                </SectionSubtitle>
              </CenteredHeader>
            </AnimateOnScroll>

            <WorkflowContrast rows={workflowContrast} accent={WORD_BLUE} theme={theme} hair={hair} isVisible={isVisible} />
          </Container>
        </Section>

        {/* 3. CROSS-APP MOMENT. Word feeds a PowerPoint slide. */}
        <Section id="cross-app">
          <Container>
            <AnimateOnScroll isVisible={isVisible('cross-header')} data-animate-id="cross-header">
              <CenteredHeader>
                <SectionEyebrow>One workflow from model to memo</SectionEyebrow>
                <SectionTitle component="h2">
                  Your memo, model, and deck, <em>on speaking terms.</em>
                </SectionTitle>
                <SectionSubtitle>
                  The same agent runs in Word, Excel, and PowerPoint, handing work between the apps.
                </SectionSubtitle>
              </CenteredHeader>
            </AnimateOnScroll>

            {/* Visual: a Word memo feeding a PowerPoint slide. */}
            <AnimateOnScroll isVisible={isVisible('cross-visual')} delay={0.1} data-animate-id="cross-visual">
              <CrossAppFlow theme={theme} />
            </AnimateOnScroll>

            <AnimateOnScroll isVisible={isVisible('cross-apps')} delay={0.16} data-animate-id="cross-apps">
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: { xs: 1, md: 2 }, mt: { xs: 4, md: 5 }, flexWrap: 'wrap' }}>
                <AppPill label="Word" color={WORD_BLUE} theme={theme} />
                <SwapHorizRounded sx={{ fontSize: 22, color: alpha(INK, 0.4) }} />
                <AppPill label="Excel" color={EXCEL_GREEN} theme={theme} />
                <SwapHorizRounded sx={{ fontSize: 22, color: alpha(INK, 0.4) }} />
                <AppPill label="PowerPoint" color={ACCENT} theme={theme} />
              </Box>
            </AnimateOnScroll>

            <AnimateOnScroll isVisible={isVisible('cross-prompts')} delay={0.2} data-animate-id="cross-prompts">
              <Box sx={{ maxWidth: 680, mx: 'auto', mt: { xs: 3, md: 3.5 }, display: 'flex', flexDirection: 'column', gap: 1 }}>
                {crossPrompts.map((p, i) => (
                  <PromptChip key={i} accent={WORD_BLUE} theme={theme} hair={hair}>{p}</PromptChip>
                ))}
              </Box>
            </AnimateOnScroll>
          </Container>
        </Section>

        {/* 4. WHAT IT DOES IN WORD. */}
        <Section id="word-capabilities">
          <Container>
            <AnimateOnScroll isVisible={isVisible('word-cap-header')} data-animate-id="word-cap-header">
              <CenteredHeader>
                <SectionEyebrow>Inside Word</SectionEyebrow>
                <SectionTitle component="h2">The IC memo and board pre-read, built in your <em>live document.</em></SectionTitle>
                <SectionSubtitle>
                  A side-panel chat that reads and edits your open document in plain English.
                </SectionSubtitle>
              </CenteredHeader>
            </AnimateOnScroll>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)' }, gap: { xs: 2, md: 2.5 }, mt: { xs: 1, md: 2 } }}>
              {wordCapabilities.map((cap, i) => (
                <AnimateOnScroll key={cap.title} isVisible={isVisible(`word-cap-${i}`)} delay={0.06 * (i + 1)} data-animate-id={`word-cap-${i}`}>
                  <Box sx={{ p: { xs: 2.5, md: 3 }, height: '100%', borderRadius: 2, border: `1px solid ${hair}`, backgroundColor: alpha(INK, 0.015), transition: 'border-color 0.2s ease, transform 0.2s ease', '&:hover': { borderColor: alpha(WORD_BLUE, 0.4), transform: 'translateY(-2px)' } }}>
                    <Box sx={{ width: 36, height: 36, borderRadius: 1.5, mb: 1.75, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', border: `1px solid ${alpha(WORD_BLUE, 0.25)}`, backgroundColor: alpha(WORD_BLUE, 0.08), color: WORD_BLUE, '& svg': { fontSize: 19 } }}>
                      {cap.icon}
                    </Box>
                    <Typography component="h3" sx={{ fontFamily: theme.typography.fontFamily, fontSize: '1rem', fontWeight: 600, color: 'text.primary', mb: 0.75, letterSpacing: '-0.01em' }}>
                      {cap.title}
                    </Typography>
                    <Typography sx={{ fontFamily: theme.typography.fontFamily, fontSize: '0.84rem', lineHeight: 1.55, color: 'text.secondary' }}>
                      {cap.desc}
                    </Typography>
                  </Box>
                </AnimateOnScroll>
              ))}
            </Box>
          </Container>
        </Section>

        {/* 5. HOW IT WORKS. The shared showcase: eyebrow, title, subtitle, and the
            four animated cards all live inside CapabilityShowcase. */}
        <CapabilityShowcase loop isVisible={isVisible} app="word" />

        {/* 6. CORE CAPABILITIES. The cross-surface bento, scoped to Word. */}
        <CoreCapabilities app="word" isVisible={isVisible} />

        {/* 7. CONNECTS TO. The same data-sources globe carried across surfaces. */}
        <Section id="word-connectors">
          <Container>
            <AnimateOnScroll isVisible={isVisible('word-connectors-header')} data-animate-id="word-connectors-header">
              <CenteredHeader>
                <SectionEyebrow>Connects to</SectionEyebrow>
                <SectionTitle component="h2">Reads your <em>sources.</em> Drafts the document.</SectionTitle>
                <SectionSubtitle>
                  Pull facts from diligence materials, filings, and your own files, under your access and with citations.
                </SectionSubtitle>
              </CenteredHeader>
            </AnimateOnScroll>
            <AnimateOnScroll isVisible={isVisible('word-connectors-globe')} delay={0.1} data-animate-id="word-connectors-globe">
              <ConnectorGlobe surface="word" />
            </AnimateOnScroll>
          </Container>
        </Section>

        {/* 8. WHO IT IS FOR + EXAMPLE PROMPTS. */}
        <Section id="word-personas">
          <Container>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '0.9fr 1.1fr' }, gap: { xs: 4, md: 7 }, alignItems: 'start' }}>
              <AnimateOnScroll isVisible={isVisible('personas')} data-animate-id="personas">
                <Box>
                  <SectionEyebrow>Who it is for</SectionEyebrow>
                  <SectionTitle component="h2" sx={{ textAlign: 'left', mt: 1.5, mb: 2 }}>
                    For the people who <em>draft and redline</em> for a living.
                  </SectionTitle>
                  <Typography sx={{ fontFamily: theme.typography.fontFamily, fontSize: '0.95rem', lineHeight: 1.65, color: 'text.secondary' }}>
                    Deal teams and consultants who live in the IC memo, the diligence report, and the board
                    pre-read - and the redlines around them.
                  </Typography>
                  {/* Maturity hedge: same line as the Excel page, same trust
                      move as the Security page's precision-over-impressive. */}
                  <Typography sx={{ fontFamily: theme.typography.fontFamily, fontSize: '0.85rem', lineHeight: 1.6, color: 'text.secondary', opacity: 0.75, mt: 1.5 }}>
                    PowerPoint is Restack's most mature surface; Excel and Word run on the same agent and are newer.
                  </Typography>
                </Box>
              </AnimateOnScroll>

              <AnimateOnScroll isVisible={isVisible('prompts')} delay={0.1} data-animate-id="prompts">
                <Box>
                  <Typography component="h3" sx={{ ...eyebrowSx, mb: 1.5 }}>Things to ask it</Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {wordPrompts.map((p, i) => (
                      <PromptChip key={`w-${i}`} accent={WORD_BLUE} theme={theme} hair={hair}>{p}</PromptChip>
                    ))}
                  </Box>
                </Box>
              </AnimateOnScroll>
            </Box>
          </Container>
        </Section>

        {/* 9. FAQ. Answers pulled by id from FAQ_REPOSITORY, so this page and the
            support widget can never drift apart. */}
        <Section id="faq">
          <Container>
            <AnimateOnScroll isVisible={isVisible('faq-header')} data-animate-id="faq-header">
              <CenteredHeader>
                <SectionEyebrow>Questions</SectionEyebrow>
                <SectionTitle component="h2">Before you <em>trial it</em>.</SectionTitle>
              </CenteredHeader>
            </AnimateOnScroll>
            <AnimateOnScroll isVisible={isVisible('faq-list')} data-animate-id="faq-list">
              <FaqList entries={WORD_FAQ} theme={theme} />
            </AnimateOnScroll>
          </Container>
        </Section>

        {/* 10. CLOSING CTA. Same wording as the hero, with the risk reversal
             directly under it and cross-surface links kept as text. */}
        <Section id="word-cta" sx={{ pb: { xs: 8, md: 12 } }}>
          <Container>
            <AnimateOnScroll isVisible={isVisible('word-cta')} data-animate-id="word-cta">
              <Box
                sx={{
                  p: { xs: 4, md: 6 }, borderRadius: '20px', textAlign: 'center', border: `1px solid ${hair}`,
                  background: `linear-gradient(135deg, ${alpha(ACCENT, 0.06)} 0%, ${alpha('#FAF9F7', 0.6)} 100%)`,
                }}
              >
                <Typography component="h2" sx={{ fontFamily: theme.typography.fontFamily, fontSize: { xs: '1.5rem', md: '2rem' }, fontWeight: 600, letterSpacing: '-0.02em', color: 'text.primary', mb: 1.5 }}>
                  Bring the memo you are drafting this week.
                </Typography>
                <Typography sx={{ maxWidth: 560, mx: 'auto', mb: 3, fontFamily: theme.typography.fontFamily, fontSize: '0.95rem', lineHeight: 1.6, color: 'text.secondary' }}>
                  We will show you Restack drafting and redlining it in your live document, with clean tracked
                  changes you accept or reject and claims that carry their source.
                </Typography>
                <CtaRow label={PRIMARY_CTA_LABEL} theme={theme} />
                <Typography sx={{ maxWidth: 560, mx: 'auto', mt: 3, fontFamily: theme.typography.fontFamily, fontSize: '0.82rem', lineHeight: 1.6, color: 'text.secondary' }}>
                  {REFUND_COPY}
                </Typography>
                {/* Cross-surface navigation stays a text link, not a second
                    button, so the band keeps exactly one primary action. */}
                <Typography sx={{ mt: 2, fontFamily: theme.typography.fontFamily, fontSize: '0.82rem', color: 'text.secondary' }}>
                  Also for <SurfaceLink to="/product">PowerPoint</SurfaceLink> and <SurfaceLink to="/excel">Excel</SurfaceLink>.
                </Typography>
              </Box>
            </AnimateOnScroll>
          </Container>
        </Section>
      </Box>

      <Footer />
    </LandingContainer>
  );
}

// ---------------------------------------------------------------------------
// Local presentational helpers.
// ---------------------------------------------------------------------------

// CROSS-APP VISUAL: a Word memo (left) feeding a PowerPoint slide (right),
// with an arrow between. Compact, glanceable, reuses the page colors.
function CrossAppFlow({ theme }) {
  return (
    <Box
      sx={{
        mt: { xs: 4, md: 5 },
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '1fr auto 1fr' },
        alignItems: 'center',
        gap: { xs: 2.5, md: 3 },
        maxWidth: 920,
        mx: 'auto',
      }}
    >
      {/* Word memo card */}
      <Box sx={{ borderRadius: '14px', overflow: 'hidden', border: `1px solid ${alpha(INK, 0.1)}`, background: '#fff', boxShadow: '0 12px 28px rgba(46,20,10,0.1)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7, px: 1.5, py: 0.85, background: WORD_BLUE }}>
          <Box sx={{ width: 14, height: 14, borderRadius: '3px', background: 'rgba(255,255,255,0.22)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.55rem', fontWeight: 800, color: '#fff' }}>W</Box>
          <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#fff', fontFamily: theme.typography.fontFamily }}>Nordic_Memo.docx</Typography>
          <Box sx={{ ml: 'auto', display: 'inline-flex', alignItems: 'center', gap: 0.4, px: 0.7, py: 0.25, borderRadius: '999px', background: 'rgba(255,255,255,0.18)', fontSize: '0.52rem', fontWeight: 700, letterSpacing: '0.04em', color: '#fff' }}>
            <TrackChangesRounded sx={{ fontSize: 10 }} /> TRACKED
          </Box>
        </Box>
        <Box sx={{ p: { xs: 2, md: 2.5 } }}>
          {/* Memo heading */}
          <Typography sx={{ fontFamily: 'Georgia, serif', fontSize: '0.82rem', fontWeight: 700, color: INK, mb: 0.25 }}>Project Nordic &middot; Diligence Memo</Typography>
          <Typography sx={{ fontFamily: theme.typography.fontFamily, fontSize: '0.58rem', fontWeight: 600, letterSpacing: '0.05em', color: alpha(INK, 0.45), textTransform: 'uppercase', mb: 1.25 }}>Confidential &middot; Draft</Typography>

          {/* Section heading */}
          <Typography sx={{ fontFamily: 'Georgia, serif', fontSize: '0.72rem', fontWeight: 700, color: WORD_BLUE, mb: 0.75 }}>1. Executive Summary</Typography>

          {/* Real-ish memo line with an inline citation superscript */}
          <Typography sx={{ fontFamily: 'Georgia, serif', fontSize: '0.68rem', lineHeight: 1.65, color: alpha(INK, 0.82), mb: 0.75 }}>
            Target revenue grew 18% to EUR 240M in FY24, led by the Nordics segment.<Box component="sup" sx={{ fontSize: '0.5rem', fontWeight: 700, color: WORD_BLUE, ml: '1px' }}>3</Box>
          </Typography>

          {/* Tracked-change sentence: red strikethrough + blue underlined insertion */}
          <Typography sx={{ fontFamily: 'Georgia, serif', fontSize: '0.68rem', lineHeight: 1.65, color: alpha(INK, 0.82), mb: 0.85 }}>
            Liability is capped at{' '}
            <Box component="span" sx={{ color: DEL_RED, textDecoration: 'line-through', textDecorationThickness: '1px' }}>the total fees paid</Box>{' '}
            <Box component="span" sx={{ color: INS_BLUE, textDecoration: 'underline', textDecorationThickness: '1px', fontWeight: 600 }}>twelve months of fees</Box>.
          </Typography>

          {/* Placeholder bars for rhythm */}
          <Box sx={{ height: 6, borderRadius: '3px', background: alpha(INK, 0.08), mb: 0.7, width: '92%' }} />
          <Box sx={{ height: 6, borderRadius: '3px', background: alpha(INK, 0.08), width: '64%' }} />
        </Box>
      </Box>

      {/* Arrow */}
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', transform: { xs: 'rotate(90deg)', md: 'none' } }}>
        <Box sx={{ width: 40, height: 40, borderRadius: '50%', background: alpha(ACCENT, 0.1), border: `1px solid ${alpha(ACCENT, 0.25)}`, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: ACCENT }}>
          <ArrowForwardRounded sx={{ fontSize: 20 }} />
        </Box>
      </Box>

      {/* PowerPoint slide card */}
      <Box sx={{ borderRadius: '14px', overflow: 'hidden', border: `1px solid ${alpha(INK, 0.1)}`, background: '#fff', boxShadow: '0 12px 28px rgba(46,20,10,0.1)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7, px: 1.5, py: 0.85, background: ACCENT }}>
          <Box sx={{ width: 14, height: 14, borderRadius: '3px', background: 'rgba(255,255,255,0.22)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.55rem', fontWeight: 800, color: '#fff' }}>P</Box>
          <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#fff', fontFamily: theme.typography.fontFamily }}>Nordic_Deck.pptx</Typography>
          <Typography sx={{ ml: 'auto', fontSize: '0.6rem', fontWeight: 600, color: 'rgba(255,255,255,0.85)', fontFamily: theme.typography.fontFamily }}>Slide 2 of 14</Typography>
        </Box>
        <Box sx={{ p: { xs: 1.5, md: 2 }, background: alpha(INK, 0.03) }}>
          <Box sx={{ background: '#fff', borderRadius: '8px', border: `1px solid ${alpha(INK, 0.07)}`, p: { xs: 1.75, md: 2 } }}>
            {/* Slide title */}
            <Typography sx={{ fontFamily: theme.typography.fontFamily, fontSize: '0.78rem', fontWeight: 700, color: INK, letterSpacing: '-0.01em', mb: 0.25 }}>Executive Summary</Typography>
            <Box sx={{ width: 28, height: 2.5, borderRadius: 2, background: ACCENT, mb: 1.25 }} />

            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'stretch' }}>
              {/* Bulleted takeaways */}
              <Box sx={{ flex: 1.15, display: 'flex', flexDirection: 'column', gap: 0.7 }}>
                {['Revenue up 18% to EUR 240M', 'Nordics drives 64% of growth', 'Liability capped at 12 months'].map((t, i) => (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.6 }}>
                    <Box sx={{ width: 4, height: 4, borderRadius: '50%', mt: '5px', flexShrink: 0, background: ACCENT }} />
                    <Typography sx={{ fontFamily: theme.typography.fontFamily, fontSize: '0.6rem', lineHeight: 1.4, color: alpha(INK, 0.78) }}>{t}</Typography>
                  </Box>
                ))}
              </Box>

              {/* Mini labelled bar chart */}
              <Box sx={{ flex: 0.85, minWidth: 78 }}>
                <Typography sx={{ fontFamily: theme.typography.fontFamily, fontSize: '0.5rem', fontWeight: 700, letterSpacing: '0.05em', color: alpha(INK, 0.5), textTransform: 'uppercase', mb: 0.5 }}>Revenue, EUR M</Typography>
                <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.5, height: 40, borderBottom: `1px solid ${alpha(INK, 0.15)}`, pb: '1px' }}>
                  {[{ h: 52, l: 'FY22' }, { h: 70, l: 'FY23' }, { h: 100, l: 'FY24' }].map((b, i) => (
                    <Box key={i} sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', height: '100%' }}>
                      <Box sx={{ width: '100%', height: `${b.h}%`, borderRadius: '2px 2px 0 0', background: i === 2 ? ACCENT : alpha(ACCENT, 0.32) }} />
                    </Box>
                  ))}
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5, mt: 0.3 }}>
                  {['FY22', 'FY23', 'FY24'].map((l, i) => (
                    <Typography key={i} sx={{ flex: 1, textAlign: 'center', fontFamily: theme.typography.fontFamily, fontSize: '0.45rem', fontWeight: 600, color: alpha(INK, 0.5) }}>{l}</Typography>
                  ))}
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function AppPill({ label, color, theme }) {
  return (
    <Box
      sx={{
        display: 'inline-flex', alignItems: 'center', gap: 0.75, px: 1.75, py: 1,
        borderRadius: '999px', border: `1px solid ${alpha(color, 0.3)}`, backgroundColor: alpha(color, 0.07),
        fontFamily: theme.typography.fontFamily, fontSize: '0.85rem', fontWeight: 600, color,
      }}
    >
      <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: color }} />
      {label}
    </Box>
  );
}

function PromptChip({ children, accent, theme, hair }) {
  return (
    <Box
      sx={{
        display: 'flex', alignItems: 'flex-start', gap: 1, px: 1.75, py: 1.1, borderRadius: 1.5,
        border: `1px solid ${hair}`, backgroundColor: alpha(INK, 0.015),
        fontFamily: theme.typography.fontFamily, fontSize: '0.85rem', lineHeight: 1.5, color: 'text.secondary',
      }}
    >
      <Box sx={{ width: 6, height: 6, borderRadius: '50%', mt: '7px', flexShrink: 0, background: accent }} />
      {children}
    </Box>
  );
}
